import get from "lodash/get";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../utils/format-sub";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import BandTag from "./BandTag";
import peopleNouns from "./peopleNouns";
import SortableHeader from "./SortableHeader";

const TYPES = [
  "payment",
  "payment_failed",
  "refund",
  "pass_purchase",
  "subscription_created",
  "subscription_cancelled",
];
// payment/pass_purchase read as money in; refund/payment_failed as money at
// risk or lost; the two subscription events carry no amount at all.
const AMOUNT_CLASS = {
  payment: "text-(--success)",
  pass_purchase: "text-(--success)",
  refund: "text-(--danger)",
  payment_failed: "text-(--danger)",
};

// The All context spans both products, so it needs a column saying which
// one each row came from; a single-product tab already knows.
const columnsFor = (context) => [
  { key: "at", labelKey: "income.transactions.date", className: "text-left!" },
  { key: "customerName", labelKey: peopleNouns(context).column },
  { key: "customerEmail", labelKey: "income.attention.email" },
  ...(context === "all"
    ? [{ key: "product", labelKey: "income.plans.product" }]
    : []),
  { key: "type", labelKey: "income.transactions.type" },
  { key: "planName", labelKey: "income.plans.plan" },
  { key: "net", labelKey: "income.transactions.amount" },
];

const TransactionsTable = ({
  transactions: initialTransactions = [],
  hasMore: initialHasMore = false,
  nextStartingAfter: initialCursor = null,
  currency = "usd",
  selector,
  context,
  organizationUser,
  periodLabel = "",
  theme = "dark",
  api,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [transactions, setTransactions] = useState(initialTransactions);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [cursor, setCursor] = useState(initialCursor);
  const [loadingMore, setLoadingMore] = useState(false);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "at",
    direction: "descending",
  });

  useEffect(() => {
    setTransactions(initialTransactions);
    setHasMore(initialHasMore);
    setCursor(initialCursor);
  }, [initialTransactions, initialHasMore, initialCursor]);

  // Type/search filtering is client-side over the rows already fetched —
  // fe-transactions returns the whole window in one page, so a filter is a
  // re-render rather than the full-page reload it used to trigger. The
  // Load more button only survives for an account big enough to overflow
  // that page.
  const loadMore = async () => {
    if (cursor == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api("fe-transactions", {
        query: {
          selector,
          context,
          ...(organizationUser && { organizationUser }),
          startingAfter: cursor,
        },
      });
      const data = await res.json();
      setTransactions((prev) => [...prev, ...(data.transactions || [])]);
      setHasMore(data.hasMore);
      setCursor(data.nextStartingAfter);
    } finally {
      setLoadingMore(false);
    }
  };

  const money = (value) => formatNumber(value || 0, currency);

  const visibleTransactions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return transactions.filter((tx) => {
      if (type && tx.type !== type) return false;
      if (
        query &&
        ![tx.customerName, tx.customerEmail, tx.planName, tx.product].some(
          (field) => (field || "").toLowerCase().includes(query)
        )
      )
        return false;
      return true;
    });
  }, [transactions, type, search]);

  const sortedTransactions = useMemo(() => {
    const sorted = [...visibleTransactions];
    sorted.sort((a, b) => {
      const aVal = get(a, sortConfig.key);
      const bVal = get(b, sortConfig.key);
      if (aVal === bVal) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp =
        typeof aVal === "string" && typeof bVal === "string"
          ? aVal.localeCompare(bVal)
          : aVal < bVal
            ? -1
            : 1;
      return sortConfig.direction === "ascending" ? cmp : -cmp;
    });
    return sorted;
  }, [visibleTransactions, sortConfig]);

  const handleSort = (key, direction) => setSortConfig({ key, direction });

  const columns = columnsFor(context);

  return (
    <>
      <header className="table-header justify-start! gap-2!">
        <hgroup>
          <h3 className="title">{t("income.transactions.title")}</h3>
        </hgroup>
        <BandTag kind="period" label={periodLabel} />
      </header>
      <hr className="mt-3" />
      <div className="transactions-table-filters my-1">
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">{t("income.transactions.allTypes")}</option>
          {TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`income.transactions.types.${value}`)}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("income.transactions.searchPlaceholder")}
        />
      </div>
      <hr className="mt-3 mb-0!" />
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" className="transactions-table">
        {/* biome-ignore lint/a11y/useValidAriaRole: table-header used for structure */}
        <thead role="table-header">
          <tr>
            {columns.map((column) => (
              <SortableHeader
                key={column.key}
                column={column}
                sortConfig={sortConfig}
                onSort={handleSort}
                t={t}
                className={column.className}
              />
            ))}
          </tr>
        </thead>
        {sortedTransactions.length > 0 ? (
          <tbody>
            {sortedTransactions.map((tx) => (
              <tr key={tx.id}>
                <td className="text-left! whitespace-nowrap">
                  {moment.unix(tx.at).format("MMM D, YYYY")}
                  <small className="table-card__subtitle">
                    {" "}
                    {moment.unix(tx.at).format("h:mm A")}
                  </small>
                </td>
                <td>
                  <div className="transactions-table__member">
                    <img
                      src={
                        tx.customerName
                          ? getAvatarUrl(
                              { name: tx.customerName, avatar: tx.avatar },
                              null,
                              theme
                            )
                          : "/img/avatars/registration-pending2.svg"
                      }
                      className={`w-8 h-8 rounded-full${
                        tx.customerName ? "" : " icon-grey"
                      }`}
                      alt={t("income.avatarAlt")}
                    />
                    <span>{tx.customerName || tx.customerEmail || "—"}</span>
                  </div>
                </td>
                <td>{tx.customerEmail || "—"}</td>
                {context === "all" && (
                  <td>{tx.product || t("income.plans.unattributed")}</td>
                )}
                <td>
                  <div className="flex items-center gap-2 justify-center">
                    <span className="pill">
                      {tx.type === "subscription_created" && tx.trial
                        ? t("income.transactions.types.trial_started")
                        : t(`income.transactions.types.${tx.type}`)}
                    </span>
                    {tx.isTrialConversion && (
                      <span className="pill" data-pill-type="trial-conversion">
                        {t("income.transactions.trialToPaid")}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {tx.planName || "—"}
                  {tx.couponCode && (
                    <small className="table-card__subtitle">
                      {" "}
                      {tx.couponCode}
                    </small>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {tx.originalAmount != null &&
                    tx.originalAmount > Math.abs(tx.net || 0) && (
                      <span className="line-through text-(--white-70) mr-1">
                        {money(
                          tx.originalAmount -
                            (tx.fee || 0) -
                            (tx.stripeFee || 0)
                        )}
                      </span>
                    )}
                  <span className={AMOUNT_CLASS[tx.type] || ""}>
                    {/* A failed attempt moved no money (net 0); show what
                        it tried to charge (sc-46061). */}
                    {tx.type === "payment_failed" && tx.gross
                      ? money(tx.gross)
                      : tx.net || tx.type === "payment"
                        ? money(Math.abs(tx.net))
                        : "—"}
                  </span>
                  {/* Net is after the MSB and Stripe fees (sc-46058); the
                      charge itself is shown under it. */}
                  {["payment", "pass_purchase"].includes(tx.type) &&
                    tx.gross > 0 &&
                    tx.gross !== tx.net && (
                      <small className="table-card__subtitle block">
                        {t("income.transactions.grossOf", {
                          amount: money(tx.gross),
                        })}
                      </small>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={columns.length}>
                <div className="body-table-empty">
                  <p>
                    {transactions.length
                      ? t("income.transactions.noResults")
                      : t("income.transactions.empty")}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>
      {hasMore && (
        <button
          type="button"
          data-button-type="secondary"
          className="transactions-table__load-more"
          onClick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore
            ? t("income.transactions.loading")
            : t("income.transactions.loadMore")}
        </button>
      )}
    </>
  );
};

export default TransactionsTable;
